/**
 * Files panel (bots B1, 2026-09-03) — a project's workspace / downloads /
 * output in the dock. Read paths reuse the /files browser's own API
 * (`/api/vault/browse`), so what this panel shows is exactly what /files
 * shows; opening a file pushes it into an artifact panel (Rule 29: see the
 * result in place). The "Run in workspace" box is the human twin of the
 * agent's run_command tool — same engine, same confinement, same audit row —
 * and its output lands in the dock as a result artifact.
 */
import { ArrowLeft, FileText, Folder, Play, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDockStore, showResultInDock } from "../../../../stores/dock";
import { toast } from "../../../../stores/toast";
import type { DockKindProps } from "../../registry";
import { Button } from "../../../common/Button";
import styles from "./FilesPanel.module.css";
import { WORKSPACE_AREAS, browsePath, commandResultMarkdown, filesData, splitArgv, type WorkspaceArea } from "./data";

interface Project { slug: string; title: string }
interface Entry { name: string; path: string; kind: "dir" | "file"; size: number; modified: string; is_hidden: boolean }

const LANG_BY_EXT: Record<string, string> = {
	md: "markdown", json: "json", py: "python", ts: "typescript", tsx: "tsx", js: "javascript", jsx: "jsx",
	yaml: "yaml", yml: "yaml", toml: "toml", sh: "bash", ps1: "powershell", css: "css", html: "html", txt: "text", csv: "csv",
};

function langFor(name: string): string {
	const ext = name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
	return LANG_BY_EXT[ext] ?? "text";
}

function fmtSize(n: number): string {
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function readDetail(res: Response): Promise<string> {
	try {
		const body = await res.json();
		const d = body?.detail;
		if (typeof d === "string") return d;
		if (d && typeof d === "object") return String(d.message ?? d.error ?? JSON.stringify(d));
	} catch { /* not json */ }
	return String(res.status);
}

export function FilesPanel({ panel }: DockKindProps) {
	const { t } = useTranslation();
	const initial = filesData(panel);
	const [project, setProject] = useState(initial.project);
	const [area, setArea] = useState<WorkspaceArea>(initial.area);
	const [sub, setSub] = useState(initial.path);
	const [projects, setProjects] = useState<Project[] | null>(null);
	const [entries, setEntries] = useState<Entry[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [cmd, setCmd] = useState("");
	const [running, setRunning] = useState(false);

	useEffect(() => {
		let cancelled = false;
		fetch("/api/projects").then(async (r) => {
			if (!r.ok) throw new Error(await readDetail(r));
			const body = await r.json();
			const list: Project[] = (Array.isArray(body) ? body : body.items ?? []).map((p: Project) => ({ slug: p.slug, title: p.title || p.slug }));
			if (!cancelled) setProjects(list);
		}).catch((e) => { if (!cancelled) { setProjects([]); setError(String(e.message ?? e)); } });
		return () => { cancelled = true; };
	}, []);

	const load = useCallback(async () => {
		if (!project) return;
		setError(null);
		try {
			const res = await fetch(`/api/vault/browse?path=${encodeURIComponent(browsePath(project, area, sub))}`);
			if (res.status === 404) { setEntries([]); return; }
			if (!res.ok) throw new Error(await readDetail(res));
			const body = await res.json();
			setEntries((body.entries ?? []).filter((e: Entry) => e.name !== ".gitkeep"));
		} catch (e) {
			setEntries([]);
			setError(String((e as Error).message ?? e));
		}
	}, [project, area, sub]);

	useEffect(() => { void load(); }, [load]);

	const title = useMemo(() => projects?.find((p) => p.slug === project)?.title ?? project, [projects, project]);

	const openFile = async (entry: Entry) => {
		try {
			const res = await fetch(`/api/vault/browse/read?path=${encodeURIComponent(entry.path)}`);
			if (!res.ok) throw new Error(await readDetail(res));
			const body = await res.json();
			const content = typeof body.content === "string" ? body.content : "";
			showResultInDock({
				id: `file:${entry.path}`,
				title: entry.name,
				content: content || t("dock.files.notText", "(binary or empty file — open it from /files)"),
				language: langFor(entry.name),
			});
		} catch (e) {
			toast.error(t("dock.files.openFailed", "Could not open file"), String((e as Error).message ?? e));
		}
	};

	const run = async () => {
		const argv = splitArgv(cmd);
		if (argv.length === 0 || running) return;
		setRunning(true);
		try {
			const res = await fetch("/api/run-command", {
				method: "POST", headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ argv, project }),
			});
			if (!res.ok) throw new Error(await readDetail(res));
			const r = await res.json();
			showResultInDock({
				id: `cmd:${project}:${Date.now()}`,
				title: `$ ${argv[0]}`,
				content: commandResultMarkdown(r),
				language: "markdown",
			});
			if (!r.ok) toast.error(t("dock.files.runFailed", "Command failed"), r.timed_out ? t("dock.files.timedOut", "timed out") : `exit ${r.exit_code}`);
			setCmd("");
			void load();
		} catch (e) {
			toast.error(t("dock.files.runRefused", "Command not run"), String((e as Error).message ?? e));
		} finally {
			setRunning(false);
		}
	};

	if (!project) {
		return (
			<div className={styles.panel} data-files-picker>
				<div className={styles.header}><Folder size={14} aria-hidden /> {t("dock.files.pick", "Pick a project")}</div>
				{projects === null && <div className={styles.muted}>{t("common.loading", "Loading…")}</div>}
				{projects && projects.length === 0 && (
					<div className={styles.muted}>{t("dock.files.noProjects", "No projects yet — create one on the Projects page; every project gets a workspace.")}</div>
				)}
				<ul className={styles.list}>
					{(projects ?? []).map((p) => (
						<li key={p.slug}>
							<button type="button" className={styles.row} aria-label={p.title} onClick={() => { setProject(p.slug); useDockStore.getState().openPanel({ ...panel, id: `files:${p.slug}`, title: `Files · ${p.slug}`, data: { project: p.slug, area: "workspace", path: "" } }); }}>
								<Folder size={14} aria-hidden /> <span className={styles.name}>{p.title}</span> <span className={styles.meta}>{p.slug}</span>
							</button>
						</li>
					))}
				</ul>
				{error && <div className={styles.error}>{error}</div>}
			</div>
		);
	}

	const parent = sub.includes("/") ? sub.slice(0, sub.lastIndexOf("/")) : "";

	return (
		<div className={styles.panel} data-files-project={project}>
			<div className={styles.header}>
				<Folder size={14} aria-hidden />
				<span className={styles.title}>{title}</span>
				<div className={styles.areas} role="tablist" aria-label={t("dock.files.areas", "Workspace areas")}>
					{WORKSPACE_AREAS.map((a) => (
						<button key={a} type="button" role="tab" aria-selected={area === a} className={`${styles.areaTab} ${area === a ? styles.areaTabActive : ""}`} onClick={() => { setArea(a); setSub(""); }}>
							{a}
						</button>
					))}
				</div>
				<button type="button" className={styles.iconBtn} title={t("common.refresh", "Refresh")} onClick={() => void load()}><RefreshCw size={13} /></button>
			</div>
			<div className={styles.crumbs}>
				{sub ? (
					<button type="button" className={styles.crumbBtn} onClick={() => setSub(parent)}><ArrowLeft size={12} /> {parent || area}</button>
				) : (
					<span className={styles.muted}>{area}/</span>
				)}
				{sub && <span className={styles.muted}> {sub}</span>}
			</div>
			{error && <div className={styles.error}>{error}</div>}
			{entries && entries.length === 0 && !error && (
				<div className={styles.muted}>{t("dock.files.empty", "Empty. An agent fills this with run_command and workspace_write; you can also drop files here from /files.")}</div>
			)}
			<ul className={styles.list}>
				{(entries ?? []).map((e) => (
					<li key={e.path}>
						<button type="button" className={styles.row} aria-label={e.kind === "dir" ? `${e.name}/` : e.name} onClick={() => (e.kind === "dir" ? setSub(sub ? `${sub}/${e.name}` : e.name) : void openFile(e))}>
							{e.kind === "dir" ? <Folder size={14} aria-hidden /> : <FileText size={14} aria-hidden />}
							<span className={styles.name}>{e.name}</span>
							<span className={styles.meta}>{e.kind === "dir" ? "" : fmtSize(e.size)}</span>
						</button>
					</li>
				))}
			</ul>
			<form className={styles.runBox} onSubmit={(ev) => { ev.preventDefault(); void run(); }}>
				<span className={styles.prompt}>$</span>
				<input
					className={styles.runInput}
					value={cmd}
					onChange={(ev) => setCmd(ev.target.value)}
					placeholder={t("dock.files.runPlaceholder", "Run in workspace/ — e.g. npm test (no shell: no pipes or redirects)")}
					aria-label={t("dock.files.runLabel", "Command to run in the workspace")}
					disabled={running}
				/>
				<Button type="submit" size="sm" variant="secondary" icon={<Play size={13} />} loading={running} disabled={!cmd.trim()}>
					{t("dock.files.run", "Run")}
				</Button>
			</form>
		</div>
	);
}
